/**
 * SignUpSignInForm presents a form to authenticate by logging in or creating an account and calls
 * the appropriate authentication function
 */
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import {
  validateEmail
} from '../../helpers/final-form-input-components'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { useBooleanState } from '../../helpers'
import {
  // NOTE: `signIn` intentionally remains exported by Authenticator for the retained
  // password-reset flow, but is no longer imported here — OTP is the only login path.
  signUp,
  signUpWithProvider,
  exchangeOTPForTokens,
  passwordlessStart,
  saveAccessToken,
  isAuthenticated,
  exchangeProviderToken,
  isProviderProofExpired,
  isEmailAlreadyExists,
  isAccountLocked,
  NO_ACCOUNT
} from '../../services/Authenticator'
import { SignUpSection } from './styles'
import { useRouter } from 'next/router'
import { useUserContext } from '../UserContext'
import { AuthHeaders } from './auth-headers'
import { useAuthContext } from './reducer'
import { SignupForm } from './signup-form'
import { SignInForm } from './signin-form'
import { TokenSelector } from './token-selector'
import {
  segmentEvent,
  trackSigninStarted,
  classifyAuthError,
  getAuthErrorMessage,
  resendCooldownKey,
  startResendCooldown,
  getResendCooldownRemaining,
  clearResendCooldown,
} from '../../helpers'
import { useFeatureValue } from '@growthbook/growthbook-react'
import { GoogleOAuthProvider } from '@react-oauth/google'

// Use useLayoutEffect on the client (runs before paint, so the experiment's channel default is
// applied without a flash of the phone input) but fall back to useEffect on the server, where
// useLayoutEffect is a no-op and would otherwise log React's "useLayoutEffect does nothing on the
// server" warning. This form DOES render during SSR on /signin and /referrals.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

// display names for the social providers, used in user-facing error copy
const PROVIDER_LABELS = { google: 'Google', apple: 'Apple' }

/**
 * providerFailure: a submit error for a social sign-in that can't proceed. The copy is
 * deliberately generic — the specific reason is carried on the Segment event instead, so
 * the UI never has to explain provider internals. `Error.userMessage` is what the shared
 * Error component renders (a plain Error has no payload for it to extract).
 *
 * @param {string} provider 'google' | 'apple'
 * @param {{reason?: string, retry?: boolean}} options `reason` is for logs/telemetry only;
 *   `retry` appends the retry prompt, for failures that a fresh authorization would fix
 * @returns {Error}
 */
const providerFailure = (provider, { reason = 'provider auth unusable', retry = false } = {}) => {
  const label = PROVIDER_LABELS[provider] ?? provider
  const error = new Error(`${reason} (${provider})`)
  error.userMessage = `Unable to continue with ${label}.${retry ? ' Please try again.' : ''}`
  return error
}

/**
 * lockedAccountFailure: the submit error for an identifier whose only accounts are locked.
 * Starbase types that rejection `accountLocked` (see its client-otp-auth skill) and carries
 * 'Account locked.' as the message; that string is accurate but reads like an internal
 * state, so the UI substitutes its own copy. Deliberately terminal and unelaborated —
 * accounts are locked for cause, and there is nothing the user can do on this screen.
 * `Error.userMessage` is what the shared Error component renders in preference to the raw
 * Starbase payload.
 *
 * @returns {Error}
 */
const lockedAccountFailure = () => {
  const error = new Error('account locked')
  error.userMessage = 'This account has been deactivated.'
  return error
}

/*
 * initSignUpForm / showHeadlines: retained for backwards compatibility with existing
 *     call sites, but no longer used — the OTP-only flow always starts on the
 *     identifier-entry screen and shows the same headers everywhere, only reaching the
 *     signup form after an OTP exchange finds no existing account.
 */
const SignUpSignInForm = ({
  onSuccess = () => null,
  // eslint-disable-next-line no-unused-vars -- kept for API compatibility
  showHeadlines = false,
  // eslint-disable-next-line no-unused-vars -- kept for API compatibility
  initSignUpForm = true,
  referredByCode = null,
}) => {
  const { state, dispatch } = useAuthContext()
  const googleClientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID

  // GrowthBook "Phone Vs Email OTP" experiment. When true, the entry screen defaults to the
  // email OTP channel instead of phone. Reading the flag here enrolls the user (fires the
  // Segment 'Experiment Viewed' exposure via growthbook.js) only when the auth entry opens —
  // the same enrollment point as mantis. Default false = phone-first (the experiment control).
  const showEmailFirst = useFeatureValue('show-email-auth-first', false)
  const desiredChannel = showEmailFirst ? 'email' : 'phone'

  // In the OTP-only flow every entry point begins on the identifier-entry screen
  // (rendered by SignInForm). The signup form is only reached after an OTP exchange
  // finds no existing account, so `formModeSignUp` always starts false regardless of
  // the retained `initSignUpForm` prop (kept for backwards-compatible call sites).
  const [formModeSignUp, setFormModeSignUp] = useState(false)
  const [termsAccepted, toggleTermsAccepted] = useBooleanState()
  const [passwordResetModal, togglePasswordResetModal] = useBooleanState(false)
  const { setUser } = useUserContext()
  const router = useRouter()
  const resetParam = router?.query?.resetPassword
  const [isDesktop, setIsDesktop] = useState(false)

  useEffect(() => {
    setIsDesktop(window.innerWidth >= 768)

    const handleResize = () => {
      if (
        (isDesktop && window.innerWidth <= 768) ||
        (!isDesktop && window.innerWidth >= 768)
      ) {
        setIsDesktop(window.innerWidth >= 768)
      }
    }

    window.addEventListener('resize', handleResize, { passive: true })
    return () => window.removeEventListener('resize', handleResize)
  }, [isDesktop])

  const setWorking = (working) => dispatch({
    type: 'SET_WORKING',
    working
  })

  const setSubmitError = (error) => {
    dispatch({
      type: 'SET_SUBMIT_ERROR',
      error
    })
  }

  // destination the OTP was sent to — used for both the exchange and the header display
  const destination = state.otpChannel === 'email' ? state.email.value : state.phone.value

  // cooldown for the current channel + destination; null until an identifier is entered
  const cooldownKey = resendCooldownKey(state.otpChannel, destination)

  const beginMinimalSignup = () => {
    // parity with mantis, which fires 'Signup Started' when the register screen mounts
    segmentEvent('Signup Started', { type: state.otpChannel })
    // move off the OTP screen and into the (now always full) create-account form
    dispatch({ type: 'TOGGLE_OTP_INPUT'})
    setFormModeSignUp(true)
  }

  const handleOTPExchange = async () => {
    try {
      segmentEvent('One Time Passcode Entered', { type: state.otpChannel })
      setSubmitError(null)
      setWorking(true)
      const tokens = await exchangeOTPForTokens(state.otp.join(''), state.otpChannel, destination)

      /*
       * The code is consumed the instant the exchange succeeds, whichever of the three
       * branches below follows. Leaving the cooldown live past this point strands a same-tab
       * sign-out/sign-in retry (or a rejected-token bounce back to /signin) behind a countdown
       * for a code that can no longer be exchanged, with a resend button the user can't press.
       */
      clearResendCooldown(cooldownKey)

      // no tokens found go to signup
      if (!tokens?.length) {
        beginMinimalSignup()
        return
      }

      // one token found
      if (tokens.length == 1) {
        const { access_token } = tokens[0]
        saveAccessToken(access_token)
        return onSuccess ? onSuccess() : router.push('/')
      }

      // multiple tokens found
      dispatch({
        type: 'SET_ACCESS_TOKENS',
        accessTokens: tokens
      })
    } catch (e) {
      segmentEvent('One Time Passcode Failed', { type: state.otpChannel, errorType: classifyAuthError(e), message: getAuthErrorMessage(e) })
      // a locked account is the one exchange failure that isn't retryable, so it gets its own
      // terminal copy rather than Starbase's raw 'Account locked.'
      setSubmitError(isAccountLocked(e) ? lockedAccountFailure() : e)
      // clear the rejected digits so the user can retry, but stay on the OTP screen with the
      // error visible (RESET_OTP_INPUT would toggle back to the entry screen and wipe the error)
      dispatch({ type: 'CLEAR_OTP' })
    } finally {
      setWorking(false)
    }
  }

  const handleSignUp = async () => {
    try {
      // OTP is the only signup path now; password is always null
      segmentEvent('Sign Up with OTP', { type: state.otpChannel })

      setSubmitError(null)
      setWorking(true)
      const user = await signUp(
        state.email.value,
        state.nameFirst.value,
        state.nameLast.value,
        state.phone.value,
        referredByCode,
        state.otp.join(''),
        null,               // no password ever
        state.otpChannel    // channel the OTP was verified on
      )

      setUser({ ...user })
      onSuccess()
    } catch (e) {
      // The most common signup failure is an email that already belongs to an account. Rather
      // than surface the raw duplicate-email error, drop back to the email sign-in screen with
      // the address prefilled so the user can sign into that account with an emailed code.
      // Only from the phone channel. On the email channel the address being submitted is the
      // one whose OTP exchange just came back empty, so "sign in with this email instead" is
      // advice we already know fails — and bouncing there would return the user to the same
      // create-account form (which renders no email field on that channel) for another lap.
      if (isEmailAlreadyExists(e) && state.otpChannel !== 'email') {
        segmentEvent('Signup Redirected To Signin', { type: state.otpChannel, reason: 'email_exists' })
        const redirect = new Error('email already exists')
        redirect.userMessage = "Looks like you already have an account. Tap Continue and we'll email you a code to sign in."
        dispatch({ type: 'REDIRECT_TO_EMAIL_SIGNIN', email: state.email.value, error: redirect })
        setFormModeSignUp(false)
        return
      }

      segmentEvent('Sign Up Failed', { type: state.otpChannel, errorType: classifyAuthError(e), message: getAuthErrorMessage(e) })
      setSubmitError(e)
    } finally {
      setWorking(false)
    }
  }

  /**
   * handleProviderSignUp: completes create-account for a provider-verified identity. The
   * provider credential (from BEGIN_PROVIDER_SIGNUP) plus the collected profile fields are
   * sent to signUpWithProvider, which creates the account and returns the authenticated user.
   * On success the user is stored and the caller's onSuccess runs. A proof that has since
   * expired can't be fixed on this screen, so it returns the user to the entry screen to
   * re-authorize; every other failure surfaces as a submit error on the form. Only reached
   * from the provider variant of SignupForm.
   *
   * @return {Promise<void>}
   */
  const handleProviderSignUp = async () => {
    const { provider, signupToken, idToken, nonce } = state.providerSignup
    try {
      segmentEvent('Sign Up with Provider', { type: provider })
      setSubmitError(null)
      setWorking(true)
      const user = await signUpWithProvider(
        provider,
        { signupToken, idToken, nonce },
        {
          nameFirst: state.nameFirst.value,
          nameLast: state.nameLast.value,
          phone: state.phone.value,
          email: state.email.value,
          referredByCode,
        },
      )
      setUser({ ...user })
      onSuccess()
    } catch (e) {
      segmentEvent('Sign Up Failed', { type: provider, errorType: classifyAuthError(e), message: getAuthErrorMessage(e) })

      // the signup handle / id_token outlives this form by only a short window. Once it's
      // gone nothing on the create-account screen can recover, so drop back to the entry
      // screen where the provider buttons (and the OTP path) are.
      if (isProviderProofExpired(e)) {
        dispatch({ type: 'END_PROVIDER_SIGNUP' })
        setFormModeSignUp(false)
        setSubmitError(providerFailure(provider, { retry: true }))
        return
      }

      setSubmitError(e)
    } finally {
      setWorking(false)
    }
  }

  /**
   * handleProviderAuth: exchanges a Google/Apple credential with the server and routes the
   * result. On success the returned access token is persisted and the caller's onSuccess (or a
   * push to '/') runs. A NO_ACCOUNT rejection means the verified identity has no Glamsquad
   * account yet, so we seed the provider-signup state and switch into the create-account form —
   * unless the proof carried no email address, in which case provider signup is impossible and
   * the user is kept on the entry screen to sign up via OTP. Any other rejection surfaces as a
   * submit error.
   *
   * @param {string} provider Provider key ('google' | 'apple')
   * @param {{code?: string, idToken?: string, nonce?: string, profile?: {nameFirst: string, nameLast: string, email: string}}} credential Credential produced by SocialButtons — Google sends `{ code }`, Apple sends `{ idToken, nonce, profile }`
   * @return {Promise<void>}
   */
  const handleProviderAuth = async (provider, credential) => {
    try {
      trackSigninStarted(provider)
      setSubmitError(null)
      setWorking(true)
      const { access_token } = await exchangeProviderToken(provider, credential)
      saveAccessToken(access_token)
      return onSuccess ? onSuccess() : router.push('/')
    } catch (e) {
      if (e?.code === NO_ACCOUNT) {
        const email = e.profile?.email ?? credential.profile?.email

        // A provider proof stands in for the OTP only for the address inside it: Starbase
        // re-derives that address from the proof and refuses to create the account under any
        // other one (models/user.js validateAndInitializeSocialSignUp). With no address in the
        // proof there is nothing it can verify, so provider signup cannot succeed — keep the
        // user on the entry screen and let them sign up with the OTP instead of collecting an
        // email the server is guaranteed to reject.
        if (!email) {
          // 'Signin Failed' (not a provider-specific name) so app and web provider failures
          // roll up together — mantis reports the same concept under this name, with `type`
          // as the discriminator. See the analytics-tracking skill's parity section.
          segmentEvent('Signin Failed', {
            type: provider,
            errorType: 'provider_no_email',
            message: 'provider returned no email address',
          })
          // no retry prompt: re-authorizing returns the same email-less proof
          setSubmitError(providerFailure(provider, { reason: 'provider returned no email address' }))
          return
        }

        segmentEvent('Signup Started', { type: provider })
        dispatch({
          type: 'BEGIN_PROVIDER_SIGNUP',
          provider,
          signupToken: e.signupToken,
          idToken: e.idToken ?? credential.idToken,
          nonce: e.nonce ?? credential.nonce,
          nameFirst: e.profile?.nameFirst ?? credential.profile?.nameFirst,
          nameLast: e.profile?.nameLast ?? credential.profile?.nameLast,
          email,
        })
        setFormModeSignUp(true)
        return
      }
      segmentEvent('Signin Failed', { type: provider, errorType: classifyAuthError(e), message: getAuthErrorMessage(e) })
      setSubmitError(e)
    } finally {
      setWorking(false)
    }
  }

  const handlePasswordlessStart = () => {
    trackSigninStarted(state.otpChannel)   // 'phone' | 'email' — fires 'Signin Started' at submit

    /*
     * A code sent to this destination less than a minute ago is still valid, so don't spend
     * another one. Skip straight to the OTP screen, where the countdown resumes from the
     * persisted deadline. Without this, a refresh lands the user back here and an ungated
     * Continue is a complete bypass of the cooldown.
     */
    const secondsRemaining = getResendCooldownRemaining(cooldownKey)
    if (secondsRemaining > 0) {
      segmentEvent('Login Code Throttled', { type: state.otpChannel, secondsRemaining })
      dispatch({ type: 'RESET_OTP_INPUT' })
      return
    }

    setSubmitError(null)
    setWorking(true)
    passwordlessStart(state.otpChannel, destination)
      .then(() => {
        // canonical success event shared with mantis: the OTP code was dispatched
        segmentEvent('Login Code Sent', { type: state.otpChannel })
        startResendCooldown(cooldownKey)
        dispatch({ type: 'RESET_OTP_INPUT' })
      })
      .catch((e) => {
        segmentEvent('Login Code Failed', { type: state.otpChannel, errorType: classifyAuthError(e), message: getAuthErrorMessage(e) })
        setSubmitError(e)
      })
      .finally(() => setWorking(false))
  }

  /**
   * handleResend: request a fresh one-time code for the identifier already in state, without
   * leaving the OTP screen. Returns whether a code actually went out, so OTPInput only starts
   * its countdown on success — a failed send must not lock the user out for a minute. The
   * cooldown is deliberately NOT written here; OTPInput owns that write via startCooldown so
   * the hook's countdown and the stored deadline can't disagree.
   *
   * @return {Promise<boolean>} true when a code was sent
   */
  const handleResend = async () => {
    try {
      setSubmitError(null)
      setWorking(true)
      await passwordlessStart(state.otpChannel, destination)
      segmentEvent('Login Code Sent', { type: state.otpChannel })
      /*
       * CLEAR_OTP, not RESET_OTP_INPUT: the latter toggles showOTPInput and would bounce the
       * user back to the identifier screen — the defect this whole change removes.
       */
      dispatch({ type: 'CLEAR_OTP' })
      return true
    } catch (e) {
      segmentEvent('Login Code Failed', { type: state.otpChannel, errorType: classifyAuthError(e), message: getAuthErrorMessage(e) })
      setSubmitError(e)
      return false
    } finally {
      setWorking(false)
    }
  }

  // switch the OTP delivery channel (phone <-> email) on the entry screen
  const switchChannel = () => {
    const channel = state.otpChannel === 'email' ? 'phone' : 'email'
    segmentEvent(channel === 'email' ? 'Use Email Code Clicked' : 'Use Text Code Clicked')
    dispatch({ type: 'SET_OTP_CHANNEL', channel })
  }

  // when coming from bpPortal to reset password this auto toggles the reset modal
  useEffect(() => {
    if (!passwordResetModal && !!resetParam) {
      togglePasswordResetModal()
    }
  }, [resetParam, passwordResetModal, togglePasswordResetModal])

  const handleOnFocus = ({ target: { name } }) =>
    dispatch({ type: 'FOCUS', name })

  const handleInput = ({ target: { name, value } }) =>
    dispatch({ type: 'CHANGE_TEXT', name, value })


  const handleOnBlur = ({ target: { name } }) => {
    const value = state[name]?.value

    // password is gone from the OTP-only flow; only email/phone are validated on blur
    if (
      (name === 'email' && !!validateEmail(value)) ||
      (name === 'phone' && !isValidPhoneNumber(value))
    ) {
      dispatch({
        type: 'ERROR',
        name,
      })
    } else {
      dispatch({
        type: 'BLUR',
        name
      })
    }
  }

  const closeSignInModal = () => {
    // removes the search from location to prevent modal from being uncloseable
    if (resetParam) {
      router.replace('/signin')
    }
    togglePasswordResetModal()
  }

  // keep the latest funnel stage in a ref so the unmount cleanup (whose closure would otherwise
  // capture stale state) can report where the user was when they left.
  const abandonStageRef = useRef({ type: state.otpChannel, step: 'identifier' })
  abandonStageRef.current = {
    type: state.otpChannel,
    step: (state.accessTokens && state.accessTokens.length)
      ? 'account_select'
      : formModeSignUp
        ? 'signup'
        : state.showOTPInput
          ? 'otp'
          : 'identifier',
  }

  useEffect(() => {
    return () => {
      // 'Signin Abandoned' — fire only if the user left WITHOUT authenticating. A successful
      // login persists an access token before this teardown, so isAuthenticated() is the
      // discriminator (the modal's onSuccess→close is otherwise indistinguishable from a
      // user dismissal). Mirrors mantis; `step` is the furthest stage reached at unmount.
      if (!isAuthenticated()) {
        segmentEvent('Signin Abandoned', {
          type: abandonStageRef.current.type,
          step: abandonStageRef.current.step,
        })
      }
      dispatch({
        type: 'RESET_FORM'
      })
    }
  }, [dispatch])

  // Establish the experiment's default OTP channel when the identifier-entry screen opens.
  // The auth reducer is app-level and resets to 'phone' on close, and GrowthBook features are
  // not loaded at app init — so the default is (re)applied here on each open. The layout effect
  // flips it before paint, so there's no flash of the phone input. Keyed on desiredChannel so a
  // late feature load (flag resolving after mount) still re-aligns. This form renders during SSR
  // on /signin and /referrals, so we use useIsomorphicLayoutEffect (useEffect on the server) to
  // avoid the "useLayoutEffect does nothing on the server" warning.
  useIsomorphicLayoutEffect(() => {
    if (
      !formModeSignUp &&
      // only apply the experiment default on the identifier-entry screen — never once a
      // code has been sent (showOTPInput) or a request is in flight (working), so a late
      // flag resolution can't flip the channel out from under an active OTP screen and
      // wipe the identifier the code was sent to
      !state.showOTPInput &&
      !state.working &&
      !(state.accessTokens && state.accessTokens.length) &&
      state.otpChannel !== desiredChannel
    ) {
      dispatch({ type: 'SET_OTP_CHANNEL', channel: desiredChannel })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desiredChannel])

  // 'Signin Shown' — fires when the identifier-entry screen is presented, and again when the
  // user switches OTP channel. `shownReadyRef` skips the brief pre-alignment render (before
  // otpChannel matches the experiment default) so this fires exactly once with the resolved
  // channel, then on every subsequent user toggle. Suppressed on the signup form and the
  // multi-account picker. Mirrors mantis.
  const shownReadyRef = useRef(false)
  useEffect(() => {
    if (formModeSignUp || (state.accessTokens && state.accessTokens.length)) { return }
    // ignore the transient pre-alignment render(s) before the default channel is applied
    if (!shownReadyRef.current && state.otpChannel !== desiredChannel) { return }
    shownReadyRef.current = true
    segmentEvent('Signin Shown', { type: state.otpChannel })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.otpChannel, desiredChannel])

  const content = (
    <>
      <AuthHeaders
        formModeSignUp={formModeSignUp}
      />

      { state.accessTokens && state.accessTokens.length > 0 ? (
        <TokenSelector onSuccess={onSuccess}/>
      ) : (
        <SignUpSection>
          {formModeSignUp ? (
            <SignupForm
              handleInput={handleInput}
              handleOnBlur={handleOnBlur}
              handleOnFocus={handleOnFocus}
              termsAccepted={termsAccepted}
              toggleTermsAccepted={toggleTermsAccepted}
              handleSignUp={handleSignUp}
              handleProviderSignUp={handleProviderSignUp}
            />
          ) : (
            <SignInForm
              handleInput={handleInput}
              handleOnFocus={handleOnFocus}
              handleOnBlur={handleOnBlur}
              handleOTPExchange={handleOTPExchange}
              handlePasswordlessStart={handlePasswordlessStart}
              handleResend={handleResend}
              switchChannel={switchChannel}
              handleProviderAuth={handleProviderAuth}
              isDesktop={isDesktop}
              passwordResetModal={passwordResetModal}
              closeSignInModal={closeSignInModal}
            />
          )}
        </SignUpSection>
      )}
    </>
  )

  return googleClientId
    ? <GoogleOAuthProvider clientId={googleClientId}>{content}</GoogleOAuthProvider>
    : content
}

export default SignUpSignInForm
